import React from 'react';
import { Account, AccountPosition } from '../types';
import TierChip from './portfolio/TierChip';
import { getTierCatalogEntryForPosition } from '../tiers/resolveTierContext';
import {
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Eye,
} from 'lucide-react';

interface RecommendationCardProps {
  recommendation: AccountPosition;
  onApprove?: (recommendationId: string) => void;
  onReject?: (recommendationId: string) => void;
  onViewDetails?: (recommendation: AccountPosition) => void;
  isUpdating?: boolean;
  account?: Account;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  onApprove,
  onReject,
  onViewDetails,
  isUpdating = false,
  account,
}) => {
  const tierEntry = getTierCatalogEntryForPosition(recommendation, account);
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RECOMMENDED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const expired = recommendation.expiresAt
    ? isExpired(recommendation.expiresAt)
    : false;
  const isBullish =
    recommendation.tradeDirection === 'BULL' ||
    recommendation.tradeDirection === 'Up';

  return (
    <div
      className={`bg-white rounded-lg border-2 p-4 transition-all hover:shadow-md ${
        expired
          ? 'border-gray-200 opacity-60'
          : 'border-gray-200 hover:border-blue-300'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {isBullish ? (
              <div title="Bullish">
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
            ) : (
              <div title="Bearish">
                <TrendingDown className="w-4 h-4 text-red-500" />
              </div>
            )}
            <span className="text-lg font-bold text-gray-900">
              {recommendation.symbol}
            </span>
            {tierEntry ? (
              <TierChip riskTier={tierEntry.riskTier} />
            ) : (
              <TierChip variant="unassigned" />
            )}
          </div>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(recommendation.status)}`}
          >
            {recommendation.status}
          </span>
        </div>

        <div className="text-right">
          <div className="text-lg font-bold text-blue-600">
            {recommendation.allocatedCapital
              ? `$${recommendation.allocatedCapital.toLocaleString()}`
              : 'N/A'}
          </div>
          <div className="text-xs text-gray-500">Capital</div>
        </div>
      </div>

      {/* Position Details */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="text-center">
          <div className="text-xs text-gray-500">Direction</div>
          <div className="text-sm font-semibold">
            {recommendation.tradeDirection}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-500">Quantity</div>
          <div className="text-sm font-semibold">
            {recommendation.targetHolding?.quantity || 'N/A'}
          </div>
        </div>
      </div>

      {/* Position Thesis - Truncated */}
      <div className="mb-3">
        <p className="text-sm text-gray-700 line-clamp-2">
          {recommendation.thesis?.originalReasoning || 'No thesis available'}
        </p>
      </div>

      {/* Position Info */}
      <div className="mb-3">
        <div className="text-xs text-gray-500 mb-1">Position Details</div>
        <div className="space-y-1">
          <div className="text-xs text-gray-700 flex items-start">
            <span className="text-blue-500 mr-1">•</span>
            <span className="line-clamp-1">
              Created: {formatDate(recommendation.createdAt)}
            </span>
          </div>
          {recommendation.targetHolding?.expectedFillPrice && (
            <div className="text-xs text-gray-700 flex items-start">
              <span className="text-green-500 mr-1">•</span>
              <span className="line-clamp-1">
                Target Price: ${recommendation.targetHolding.expectedFillPrice}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 text-xs text-gray-500">
          {recommendation.expiresAt && (
            <div className="flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>Expires {formatDate(recommendation.expiresAt)}</span>
            </div>
          )}
          {expired && (
            <div className="flex items-center space-x-1 text-red-500">
              <AlertTriangle className="w-3 h-3" />
              <span>Expired</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          {onViewDetails && (
            <button
              onClick={() => onViewDetails(recommendation)}
              className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Eye className="w-3 h-3 mr-1 inline" />
              Details
            </button>
          )}
          {!expired && onApprove && onReject && (
            <>
              <button
                onClick={() => onReject(recommendation._id)}
                disabled={isUpdating}
                className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 border border-red-300 rounded hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject
              </button>
              <button
                onClick={() => onApprove(recommendation._id)}
                disabled={isUpdating}
                className="px-3 py-1 text-xs font-medium text-white bg-green-600 border border-transparent rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? 'Processing...' : 'Approve'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationCard;
